import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { ShieldCheck, MapPin, ArrowRight, Users, Monitor } from 'lucide-react';
import { SupervisorCard } from './SupervisorDirectory';
import { useJsonLd, SITE_URL } from './useJsonLd';
import { STATE_MAP } from './stateData';

const StateSupervisors = () => {
  const { state: slug } = useParams();
  const info = STATE_MAP[slug];
  const [supervisors, setSupervisors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!info) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/supervisors`, { params: { state: info.name } });
      setSupervisors(res.data.supervisors || []);
      setTotal(res.data.total || 0);
    } catch (e) { setSupervisors([]); } finally { setLoading(false); }
  }, [info]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!info) return;
    document.title = `Clinical Supervisors in ${info.name} | LCSW Supervision | I Need A Social Worker`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = `Find clinical supervisors in ${info.name} offering online and in-person social work supervision toward LCSW licensure. Compare LCSW-S supervisors near you.`;
  }, [info]);

  const faqs = info ? [
    { q: `How do I find an LCSW supervisor in ${info.name}?`, a: `Browse verified clinical supervisors on I Need A Social Worker, filter by city, format (online or in person), and specialty, then send a secure inquiry to start supervision toward your LCSW in ${info.name}.` },
    { q: `Is online clinical supervision available in ${info.name}?`, a: `Yes. Many supervisors offer online, in-person, or hybrid supervision so you can find a format that fits your schedule anywhere in ${info.name}.` },
    { q: `How much does clinical supervision cost in ${info.name}?`, a: `Rates vary by supervisor and experience. Each profile lists the per-session rate so you can compare options across ${info.name}.` },
  ] : [];

  useJsonLd('state-supervisor-collection', info ? {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `Clinical Supervisors in ${info.name}`,
    description: `Directory of clinical supervisors offering online and in-person social work supervision toward LCSW licensure in ${info.name}.`,
    url: `${SITE_URL}/${slug}/clinical-supervisors`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: supervisors.filter((s) => !s.is_demo).map((s, i) => ({
        '@type': 'ListItem', position: i + 1,
        item: {
          '@type': 'Person', name: s.full_name, jobTitle: `${s.license_type} Clinical Supervisor`,
          url: `${SITE_URL}/supervisors/${s.user_id}`,
          address: { '@type': 'PostalAddress', addressRegion: info.name, addressCountry: 'US' },
        },
      })),
    },
  } : null);

  useJsonLd('state-supervisor-faq', info ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  } : null);

  if (!info) return <Navigate to="/find-a-supervisor" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="relative pt-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-white">
          <p className="text-secondary font-medium tracking-widest uppercase text-sm mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> {info.name} · LCSW-S Supervision
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold" data-testid="state-supervisor-title">
            Find a Clinical Supervisor in {info.name}
          </h1>
          <p className="text-white/90 mt-4 max-w-2xl text-base sm:text-lg">
            Connect with clinical supervisors offering online and in-person social work supervision toward your LCSW in {info.name}. Compare specialties, formats, and rates — then reach out securely.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/find-a-supervisor"><Button className="bg-secondary hover:bg-secondary-hover text-white" data-testid="state-view-all-supervisors">View all supervisors <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/become-a-supervisor"><Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary" data-testid="state-become-supervisor">Become a Listed Supervisor</Button></Link>
          </div>
        </div>
      </section>

      {/* City chips */}
      <section className="bg-white border-b py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-gray-500 mb-3">Clinical supervision across {info.name}:</p>
          <div className="flex flex-wrap gap-2">
            {info.cities.map((c) => (
              <span key={c} className="px-4 py-2 bg-primary/5 border border-primary/15 rounded-lg text-primary text-sm font-medium" data-testid={`state-city-${c}`}>
                Supervisors in {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl font-bold text-primary">Supervisors in {info.name}</h2>
          <span className="text-sm text-gray-500" data-testid="state-supervisor-count">{loading ? 'Loading…' : `${total} result${total === 1 ? '' : 's'}`}</span>
        </div>

        {!loading && supervisors.length === 0 ? (
          <div className="bg-white rounded-2xl border p-10 text-center" data-testid="state-no-supervisors">
            <ShieldCheck className="w-10 h-10 text-secondary mx-auto mb-3" />
            <h3 className="font-serif text-xl text-primary font-bold mb-1">Supervisors in {info.name} are coming soon</h3>
            <p className="text-gray-500 mb-5">Are you an LCSW-S in {info.name}? Get listed and connect with social workers seeking supervision.</p>
            <Link to="/become-a-supervisor"><Button className="bg-primary text-white">Become a Listed Supervisor</Button></Link>
          </div>
        ) : (
          <div className="space-y-4">
            {supervisors.map((s) => <SupervisorCard key={s.user_id} s={s} />)}
          </div>
        )}
      </section>

      {/* Why + FAQ */}
      <section className="bg-white border-t py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            <Feature icon={ShieldCheck} title="Verified Credentials" text={`Supervisors' licenses are reviewed${info.verifiedBoard ? ` through ${info.verifiedBoard}` : ''}.`} />
            <Feature icon={Monitor} title="Online or In Person" text={`Flexible supervision formats across ${info.name}.`} />
            <Feature icon={Users} title="Right Fit" text="Compare specialties and populations served." />
          </div>
          <h2 className="font-serif text-2xl font-bold text-primary mb-6">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {faqs.map((f) => (
              <div key={f.q} data-testid="state-supervisor-faq-item">
                <p className="font-semibold text-gray-900">{f.q}</p>
                <p className="text-gray-600 mt-1">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const Feature = ({ icon: Icon, title, text }) => (
  <div className="text-center">
    <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3"><Icon className="w-5 h-5 text-primary" /></div>
    <p className="font-semibold text-primary">{title}</p>
    <p className="text-sm text-gray-500 mt-1">{text}</p>
  </div>
);

export default StateSupervisors;
